import React from 'react';
import { Star } from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import { OwnerAvatar } from '../kit/OwnerChip';
import { formatPhone } from '../shared/phone';
import { CHANNEL_META, channelIcon, formatRowDate, rowPreview } from './inboxFormat';

// Ticket 142: one row per contact. Name falls back to a formatted phone,
// then the bare contact_id — never blank.
function rowName(item) {
  return item.display_name || (item.phone ? formatPhone(item.phone) : null) || item.contact_id;
}

export default function InboxRow({ item, selected, onClick, checked = false, onCheck, onToggleStar, starBusy = false }) {
  const unread = item.unread || 0;
  const name = rowName(item);
  const LastIcon = channelIcon(item.last_channel);
  const otherChannels = (item.channels || []).filter((ch) => ch !== item.last_channel);

  const starred = !!item.starred;

  return (
    <li className={`flex items-start hover:bg-slate-50 transition ${selected || checked ? 'bg-[#070B5F]/5' : ''}`}>
      {/* Ticket 195: select box, outside the row button so a click never opens the thread. */}
      <span className="pl-3 pt-4 flex-shrink-0">
        <Checkbox
          checked={checked}
          onCheckedChange={(v) => onCheck?.(item.contact_id, v === true)}
          aria-label={`Select ${name}`}
          data-testid={`inbox-check-${item.contact_id}`}
        />
      </span>
      <button
        type="button"
        onClick={onClick}
        data-testid={`inbox-row-${item.contact_id}`}
        className="min-w-0 flex-1 text-left p-3 flex gap-3 items-start"
      >
        <span className="relative flex-shrink-0">
          <OwnerAvatar userId={item.contact_id} name={name} size="md" />
          <span
            className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500"
            aria-hidden="true"
          >
            <LastIcon size={9} />
          </span>
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center justify-between gap-2">
            <span className={`text-sm truncate ${unread > 0 ? 'font-semibold text-slate-900' : 'font-medium text-slate-800'}`}>
              {name}
            </span>
            <span className="text-[10px] text-slate-400 flex-shrink-0 whitespace-nowrap">{formatRowDate(item.latest_at)}</span>
          </span>
          {item.match?.snippet && (
            // Ticket 195: the message the search matched, shown under the row's own preview.
            <span className="block text-[11px] text-slate-500 italic truncate mt-0.5" data-testid={`inbox-match-${item.contact_id}`}>
              {item.match.snippet}
            </span>
          )}
          <span className="flex items-center justify-between gap-2 mt-0.5">
            <span className={`text-xs truncate ${unread > 0 ? 'text-slate-700 font-medium' : 'text-slate-500'}`}>
              {rowPreview(item)}
            </span>
            <span className="flex items-center gap-1 flex-shrink-0">
              {otherChannels.map((ch) => {
                const Icon = (CHANNEL_META[ch] || {}).icon || LastIcon;
                return <Icon key={ch} size={10} className="text-slate-400" aria-hidden="true" />;
              })}
              {item.source === 'ghl' && (
                <span
                  className="text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700"
                  data-testid={`ghl-badge-${item.contact_id}`}
                >
                  GHL
                </span>
              )}
              {unread > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full text-white" style={{ background: '#070B5F' }}>
                  {unread}
                </span>
              )}
            </span>
          </span>
        </span>
      </button>
      <button
        type="button"
        onClick={() => onToggleStar?.(item.contact_id, !starred)}
        disabled={starBusy}
        aria-label={starred ? `Unstar ${name}` : `Star ${name}`}
        aria-pressed={starred}
        data-testid={`inbox-star-${item.contact_id}`}
        className="p-3 flex-shrink-0 disabled:opacity-50"
      >
        <Star size={14} className={starred ? 'text-amber-500 fill-amber-400' : 'text-slate-300 hover:text-slate-500'} />
      </button>
    </li>
  );
}
